import React, { useState, FC } from 'react';
import { useRange, UseRangeProps } from 'react-instantsearch';
import { useDebounce } from 'react-use';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import styles from './RefinementPrice.module.scss';

const unsetNumberInputValue = '';

export const RefinementPrice: FC<UseRangeProps> = (props: UseRangeProps) => {
    const { start, range, canRefine, refine } = useRange(props);
    const step = 1;
    const values = {
        min: start[0] !== -Infinity && start[0] !== range.min ? start[0] : unsetNumberInputValue,
        max: start[1] !== Infinity && start[1] !== range.max ? start[1] : unsetNumberInputValue,
    };
    const [prevValues, setPrevValues] = useState(values);
    const { trackFilter } = useDrawerFilterTracking();
    const [{ from, to }, setRange] = useState({
        from: values.min?.toString(),
        to: values.max?.toString(),
    });

    if (values.min !== prevValues.min || values.max !== prevValues.max) {
        setRange({ from: values.min?.toString(), to: values.max?.toString() });
        setPrevValues(values);
    }

    function doRefine() {
        const nextMin = from ? Number(from) : undefined;
        const nextMax = to ? Number(to) : undefined;
        const currentMin = values.min === unsetNumberInputValue ? undefined : values.min;
        const currentMax = values.max === unsetNumberInputValue ? undefined : values.max;

        if (nextMin !== currentMin || nextMax !== currentMax) {
            trackFilter('cust_effectivePrice', [nextMin, nextMax].toString(), { replace: true });
        }

        refine([nextMin, nextMax]);
    }

    useDebounce(
        () => {
            doRefine();
        },
        1000,
        [from, to],
    );

    if (!canRefine) return <></>;
    return (
        <form
            className={styles.form}
            name="refinePriceRange"
            onSubmit={(event) => {
                event.preventDefault();
                doRefine();
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.requestSubmit();
                }
            }}
        >
            <div className={styles.box}>
                <label className={styles.input}>
                    <input
                        type="number"
                        min={range.min}
                        max={range.max}
                        inputMode="numeric"
                        value={stripLeadingZeroFromInput(from || unsetNumberInputValue)}
                        step={step}
                        placeholder={range.min?.toString()}
                        disabled={!canRefine}
                        onInput={({ currentTarget }) => {
                            const value = currentTarget.value;

                            setRange({ from: value || unsetNumberInputValue, to });
                        }}
                    />
                </label>
                <span className={styles.dash} />
                <label className={styles.input}>
                    <input
                        type="number"
                        min={range.min}
                        max={range.max}
                        inputMode="numeric"
                        value={stripLeadingZeroFromInput(to || unsetNumberInputValue)}
                        step={step}
                        placeholder={range.max?.toString()}
                        disabled={!canRefine}
                        onInput={({ currentTarget }) => {
                            const value = currentTarget.value;

                            setRange({ from, to: value || unsetNumberInputValue });
                        }}
                    />
                </label>
            </div>
        </form>
    );
};

function stripLeadingZeroFromInput(value: string): string {
    return value.replace(/^(0+)\d/, (part) => Number(part).toString());
}
